/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { takeUntil } from 'rxjs/operators';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { cssVar } from '@touchplan/css-var';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { PromisePeriod } from '../../../../app-data/models/PromisePeriod';
import { ActiveLine } from '../../../../app-data/models/ActiveLine';
import { ComponentManager } from '@appCanvasCommon/component-system/ComponentManager';
import { GenericPool } from '../../../../app-data/models/data-types/GenericPool';
import {
	ITimelinePromisePeriod,
	PeriodDragDirection,
	promisePeriodLineFactory,
} from '@appPlanApplication/timeline/TimelineHelpers';
import { PROMISE_PERIOD_LAST_PERIOD, PROMISE_PERIOD_NEXT_PERIOD } from '@touchplan/strings';
import { TimelineDisplayItem } from '@appPlanApplication/timeline/tickets/TimelineDisplayItem';
import { Texture, utils } from 'pixi.js-legacy';
import { TICKET_TEXTURE_OPTIONS } from '@appPlanApplication/tickets/TICKET_TEXTURE_OPTIONS';
import { DefaultSplitDataRenderType } from '@touchplan/common';
import { PromisePeriodTap } from '@appPlanApplication/timeline/promise-period/PromisePeriodTap';
import { PromisePeriodDrag } from '@appPlanApplication/timeline/promise-period/PromisePeriodDrag';
import { IPromisePeriodSpatialData } from '@appPlanApplication/timeline/promise-period/IPromisePeriodSpatialData';
import { IDrawAndAttachPeriod } from '@appPlanApplication/timeline/promise-period/IDrawAndAttachPeriod';

const settings = {
	// The color of the last (previous) period
	lastPeriodColor: cssVar.promisePeriodLastColor,
	// The opacity of the last (previous) period
	lastPeriodAlpha: cssVar.promisePeriodLastAlpha,
	// The color of the next period
	nextPeriodColor: cssVar.promisePeriodNextColor,
	// The opacity of the next period
	nextPeriodAlpha: cssVar.promisePeriodNextAlpha,
	// The opacity when minimized
	minimizedAlpha: cssVar.promisePeriodMinimizedAlpha,
	// The height of the line when minimized
	minimizedLineHeight: cssVar.promisePeriodMinimizedHeight_px,
	// The size of the chevron drag handles. Was 24px, increased to 32px for larger timeline height
	dragHandleSize: 32,
	textSize: cssVar.promisePeriodTextSize_px,
	// The padding for the drag handles (used to calculate if they should be hidden or not when scaled)
	dragHandlePadding: 20,
	// Vertical offset for the Promise Now Button
	promiseNowYOffset: 32,
	// Horizontal padding around the promise now button (used for visibility calculation)
	promiseNowXPadding: 20,
};

/**
 * PromisePeriodHeaderDrawer
 * This draws the promise period timeline header and controls the left/right drag handles
 */
export class PromisePeriodHeaderDrawer extends AbstractAreaDrawer {
	/**
	 * A cache of promise period service event emissions
	 * We use this to draw the previous and next periods
	 * @private
	 */
	private _cachedPromisePeriod: Map<string, PromisePeriod> | null = null;
	/**
	 * A cache of the active line. The reason why we collect this data is because we anchor our promise period at
	 * the active line position, then draw as an offset from the line.
	 * @private
	 */
	private _cachedActiveLine: ActiveLine | null = null;
	/**
	 * Instance of our ComponentManager. This is the thing that handles interaction events and other behaviour
	 * @private
	 */
	private _components: ComponentManager;
	/**
	 * A pool of simple 1px all white texture objects. We use this to draw all of our promise period header elements.
	 * @private
	 */
	private _promisePeriodPool: GenericPool<TimelineDisplayItem<ITimelinePromisePeriod>> = new GenericPool<
		TimelineDisplayItem<ITimelinePromisePeriod>
	>(promisePeriodLineFactory, this._container);
	/**
	 * A pool of TimelineDisplayItem that are used to handle the drag functionality
	 * Even though this signature is similar to the _promisePeriodPool, we maintain two separate pools because the interactions
	 * will be different between the two.
	 * @private
	 */
	private _dragHandlePool: GenericPool<TimelineDisplayItem<ITimelinePromisePeriod>> = new GenericPool<
		TimelineDisplayItem<ITimelinePromisePeriod>
	>(promisePeriodLineFactory, this._container);
	/**
	 * Creates a new instance of a PromisePeriodHeader
	 * @param _canvasAngularLand
	 * @param parentApplication
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, parentApplication: AbstractApplication) {
		super(parentApplication, LayerPaths.UI_TIMELINE_PROMISE_PERIOD);
		this._components = new ComponentManager(this);
		this.setupSubscribers();
	}
	/**
	 * Draw the promise period header elements
	 * @param promisePeriodData
	 */
	public override draw(
		promisePeriodData: Map<string, PromisePeriod> | null = this._cachedPromisePeriod
	): void {
		if (!promisePeriodData || !this._cachedActiveLine) {
			this.hide();
			return;
		}

		// If the active line is in edit mode, do not redraw the promise period
		if (this._cachedActiveLine.currentRenderType !== DefaultSplitDataRenderType.database) {
			return;
		}

		super.draw();
		this._promisePeriodPool.reset();
		this._dragHandlePool.reset();

		const lastId: string = this._canvasAngularLand.promisePeriodService.lastPromisePeriodId;
		const nextId: string = this._canvasAngularLand.promisePeriodService.currentPromisePeriodId;
		this.tryDrawHeader(
			lastId,
			settings.lastPeriodColor,
			settings.lastPeriodAlpha,
			PROMISE_PERIOD_LAST_PERIOD
		);
		this.tryDrawHeader(
			nextId,
			settings.nextPeriodColor,
			settings.nextPeriodAlpha,
			PROMISE_PERIOD_NEXT_PERIOD
		);
		this.updateButtonPosition();

		this.parentApplication.markAsDirty();
	}
	/**
	 * Updates the position of the container.
	 * @param _ The time since the last frame, not used.
	 */
	public override update(_?: number): void {
		if (!this._cachedActiveLine) {
			return;
		}

		// If the active line is in edit mode, do not update the promise period because we anchor the period to the active line
		// this will cause the promise period to shift as we move the active line over to the pull side
		if (this._cachedActiveLine.currentRenderType !== DefaultSplitDataRenderType.database) {
			return;
		}
		const activeLine: ActiveLine = this._cachedActiveLine;
		const screenX: number = this.parentApplication.camera.realToScreenX(activeLine.view.activeLineX);
		super.update(screenX, cssVar.headerHeight_px);
	}

	/**
	 * Destroy all the things. We need to override the abstract destroy so we can clear up our objects specific to this model
	 * @param options
	 */
	public override destroy(options: DestroyOptions = DestroyOptions.includeChildren): void {
		super.destroy(options);
		this._promisePeriodPool.destroy(options);
		this._dragHandlePool.destroy(options);
		this._components.destroy(options);
		this._cachedPromisePeriod = null;
		this._cachedActiveLine = null;
	}

	/**
	 * A helper method for drawing headers. This will only draw a header if provided conditions are met
	 * @param periodId The promise period ID to draw
	 * @param color The color of the promise period
	 * @param alpha The opacity of the promise period
	 * @param labelText The text to provide to the label
	 * @private
	 */
	private tryDrawHeader(periodId: string, color: string, alpha: number, labelText: string): void {
		const period: PromisePeriod | undefined = this._cachedPromisePeriod?.get(periodId);
		if (!period) {
			return;
		}

		this.drawHeader(period, color, alpha, labelText);
	}

	/**
	 * Helper method which dispatches new IPromiseNowPosition data to position the UI button. This calculates the visibility
	 * of the promise now button and sets the position. Steps:
	 * 	1. If there is no current period, dispatch 0,0 with a visibility of false because there is nothing to show
	 * 	2. Calculate the screen width of the current period (take the endX and subtract the startX)
	 * 	3. Calculate the scaled screen width by multiplying the screen width by camera scale
	 * 	4. If the button does not fit in the promise period width, the button should not be visible
	 * 	5. Then calculate the X position by adding the startX with half of the period width with half the button width (to center it all)
	 * 	6. For the Y position, add the header height, timeline height and offset
	 * @private
	 */
	private updateButtonPosition(): void {
		const nextId: string = this._canvasAngularLand.promisePeriodService.currentPromisePeriodId;
		if (!nextId) {
			this._canvasAngularLand.promisePeriodService.setButtonPosition(0, 0, false);
			return;
		}
		const period: PromisePeriod = this._cachedPromisePeriod?.get(nextId) as PromisePeriod;
		if (!period) {
			return;
		}
		const spatialData: IPromisePeriodSpatialData = this.getSpatialData(period);

		const widthOnScreen: number = spatialData.endScreenX - spatialData.startScreenX;
		const buttonWidth: number = cssVar.promisePeriodButtonWidth_px + settings.promiseNowXPadding * 2;
		const isVisible: boolean = buttonWidth <= widthOnScreen;
		const screenX: number =
			spatialData.startScreenX + widthOnScreen / 2 - cssVar.promisePeriodButtonWidth_px / 2;
		const screenY: number = cssVar.headerHeight_px + cssVar.timelineHeight_px + settings.promiseNowYOffset;
		this._canvasAngularLand.promisePeriodService.setButtonPosition(screenX, screenY, isVisible);
	}
	/**
	 * Setup all internal subscriptions that causes this drawer to re-draw
	 * @private
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand.promisePeriodService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((periods: Map<string, PromisePeriod> | null) => {
				this._cachedPromisePeriod = periods;
				this.update();
				this.draw();
			});

		this._canvasAngularLand.activeLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((activeLine) => {
				this._cachedActiveLine = activeLine;
				this.update();
				this.draw();
			});

		this.parentApplication.camera.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((changedEvent) => {
				this.update();
				this.draw();
			});
	}

	/**
	 * Draws the promise period header
	 * @param promisePeriod The PromisePeriod model to draw
	 * @param color The color to draw this promise period
	 * @param alpha the alpha value for this promise period
	 * @private
	 */
	private drawHeader(
		promisePeriod: PromisePeriod,
		color: string,
		alpha: number = settings.lastPeriodAlpha,
		labelText: string = PROMISE_PERIOD_NEXT_PERIOD
	): void {
		if (promisePeriod.view.minimized) {
			this.drawMinimizedHeader(promisePeriod, color);
			return;
		}

		const element: TimelineDisplayItem<ITimelinePromisePeriod> = this.drawAndAttachRegion(promisePeriod, {
			color,
			alpha,
			height: cssVar.timelineHeight_px,
		});
		element.label.visible = true;
		element.setLabel(labelText, cssVar.white, settings.textSize);
		element.label.position.set(element.width / 2, element.height / 2);
		element.label.style.wordWrapWidth = element.width;

		const labelScaledWidth: number = element.label.width * this.parentApplication.camera.scale.x;
		const elementScaledWidth: number = element.width * this.parentApplication.camera.scale.x;
		if (labelScaledWidth >= elementScaledWidth) {
			element.label.visible = false;
		}

		const dragHandleScaledWidth: number = settings.dragHandleSize * this.parentApplication.camera.scale.x;
		const totalRequiredWidth: number =
			labelScaledWidth + dragHandleScaledWidth * 2 + settings.dragHandlePadding;
		const shouldShowHandles: boolean = totalRequiredWidth <= elementScaledWidth;

		if (!shouldShowHandles) {
			return;
		}

		// Only draw the left handle when:
		// 1. We currently have a last/current promise period
		// 2. The input period is not the last/current promise period
		if (
			promisePeriod.$id !== this._canvasAngularLand.promisePeriodService.lastPromisePeriodId &&
			!this._canvasAngularLand.promisePeriodService.lastPromisePeriodId
		) {
			const leftHandle: TimelineDisplayItem<ITimelinePromisePeriod> = this.drawAndAttachDragHandle(
				promisePeriod,
				utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_TIMELINE_CHEVRON_LEFT],
				PeriodDragDirection.Left
			);
		}

		// Only draw the right handle if the input period is the expected current period, otherwise early exit
		if (promisePeriod.$id !== this._canvasAngularLand.promisePeriodService.currentPromisePeriodId) {
			return;
		}

		const spatialData: IPromisePeriodSpatialData = this.getSpatialData(promisePeriod);
		const rightHandle: TimelineDisplayItem<ITimelinePromisePeriod> = this.drawAndAttachDragHandle(
			promisePeriod,
			utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_TIMELINE_CHEVRON_RIGHT],
			PeriodDragDirection.Right
		);
		rightHandle.x = spatialData.endScreenX - this._container.x - rightHandle.width;
	}

	/**
	 * Draws the drag handles and attaches the promise period data
	 * @param promisePeriod
	 * @param texture
	 * @private
	 */
	private drawAndAttachDragHandle(
		promisePeriod: PromisePeriod,
		texture: Texture,
		dragDirection: PeriodDragDirection
	): TimelineDisplayItem<ITimelinePromisePeriod> {
		const spatialData: IPromisePeriodSpatialData = this.getSpatialData(promisePeriod);
		const handle: TimelineDisplayItem<ITimelinePromisePeriod> = this._dragHandlePool.grab();
		handle.setTexture(texture);
		this.trySetupHandleComponents(handle);
		handle.setData({ period: promisePeriod, spatialData, dragDirection });
		handle.width = settings.dragHandleSize;
		handle.height = settings.dragHandleSize;
		handle.x = spatialData.startScreenX - this._container.x;
		handle.y = cssVar.timelineHeight_px / 2 - handle.height / 2;
		return handle;
	}

	/**
	 * Draws the promise period header (in minimized mode)
	 * @param promisePeriod The PromisePeriod model to draw
	 * @param color The color to draw this promise period
	 * @param alpha the alpha value for this promise period
	 * @private
	 */
	private drawMinimizedHeader(promisePeriod: PromisePeriod, color: string): void {
		// Begin drawing the indicator line at the top of the region
		this.drawAndAttachRegion(promisePeriod, {
			color,
			alpha: settings.lastPeriodAlpha,
			height: settings.minimizedLineHeight,
		});

		// Begin drawing the full region rectangle
		this.drawAndAttachRegion(promisePeriod, {
			color,
			alpha: settings.minimizedAlpha,
			height: cssVar.timelineHeight_px,
		});
	}

	/**
	 * Helper method to draw and attach data to a promise region in the view.
	 * This is to prevent a bunch of duplicate code when drawing similar things.
	 * @param period The promiese period to attach
	 * @param config Configuration settings for the element
	 * @return TimelineDisplayItem<ITimelinePromisePeriod> the cached object
	 * @private
	 */
	private drawAndAttachRegion(
		period: PromisePeriod,
		config: IDrawAndAttachPeriod
	): TimelineDisplayItem<ITimelinePromisePeriod> {
		const spatialData: IPromisePeriodSpatialData = this.getSpatialData(period);
		const thing: TimelineDisplayItem<ITimelinePromisePeriod> = this._promisePeriodPool.grab();
		thing.sprite.alpha = config.alpha;
		thing.setData({ period, spatialData });
		thing.setTexture(Texture.WHITE);
		thing.tint = config.color;
		thing.width = spatialData.endScreenX - spatialData.startScreenX;
		thing.height = config.height;
		thing.x = spatialData.startScreenX - this._container.x;
		thing.y = 0;
		this.trySetupHeaderComponents(thing);
		return thing;
	}
	/**
	 * Helper method to calculate screen and world spatial data for a given promise period
	 * @param promisePeriod The promise period object to calcualte spatial data for.
	 * @private
	 */
	private getSpatialData(promisePeriod: PromisePeriod): IPromisePeriodSpatialData {
		const startWorldX: number =
			this._canvasAngularLand.activeColumnService.getXFromDate(promisePeriod.view.startDate as string) -
			this._canvasAngularLand.activeColumnService.columnWidth;
		const endWorldX: number = this._canvasAngularLand.activeColumnService.getXFromDate(
			promisePeriod.view.endDate as string
		);
		const startScreenX: number = this.parentApplication.camera.realToScreenX(startWorldX);
		const endScreenX: number = this.parentApplication.camera.realToScreenX(endWorldX);
		return {
			startScreenX,
			endScreenX,
			startWorldX,
			endWorldX,
		};
	}
	/**
	 * Attempts to connect the `PromisePeriodTap` gesture to the provided pooled TimelineDisplayItem
	 * If there is already a component attached, it will silently return and do nothing.
	 * @param item
	 * @private
	 */
	private trySetupHeaderComponents(item: TimelineDisplayItem<ITimelinePromisePeriod>): void {
		if (item.components.count) {
			return;
		}

		item.components.add(`promisePeriodTap`, new PromisePeriodTap(), this._canvasAngularLand, item);
	}
	/**
	 * Attempts to connect the `PromisePeriodDrag` gesture to the provided pooled TimelineDisplayItem
	 * If there is already a component attached, it will silently return and do nothing.
	 * @param item
	 * @private
	 */
	private trySetupHandleComponents(item: TimelineDisplayItem<ITimelinePromisePeriod>): void {
		if (item.components.count) {
			return;
		}

		item.components.add(`promisePeriodDrag`, new PromisePeriodDrag(), this._canvasAngularLand, item);
	}
}
